import React from 'react';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { loginOrSignup } from '@gik/auth/utils/auth';
import { followPage, removeOrUnfollowPage } from '@gik/api/users/user';
import { Badge, HoverAnimation, SvgIcon } from '@gik/ui';
import { useUserStore } from '@gik/core/store/UserStore';
import { isInkindPageOwnerOrOrganizer, isUserFollowingPage } from '@gik/inkind-page/utils/inkindPagePermissions';
import { useUserPages } from '@gik/api/inkinds/userPages';
import { useInkind } from '@gik/api/inkinds/inkind';
import { optimisticUpdate } from '@gik/core/api';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '@gik/inkind-page/i18n/en';

export interface IFollowPageCTAProps {
  inkindRouteId: string;
  className?: string;
}

const blockName = 'follow-page-cta';

export default function FollowPageCTA({ inkindRouteId, className }: IFollowPageCTAProps) {
  const bem = useBemCN(blockName);
  const { t } = useTranslation();

  const { data: inkindPage } = useInkind(inkindRouteId);
  const userId = useUserStore(state => state.id);
  const isPageOwnerOrOrganizer = isInkindPageOwnerOrOrganizer(inkindPage, userId);
  const { data: userPages, mutate: mutateUserPages } = useUserPages(userId);
  const isFollowing = isUserFollowingPage(inkindPage, userId, userPages);

  async function handleFollowPage(recursiveBreak?: boolean) {
    if (!userId && recursiveBreak) {
      return;
    }

    if (!userId) {
      await loginOrSignup();
      await handleFollowPage(true);
    } else {
      if (isFollowing) {
        return;
      }
      await optimisticUpdate(
        [...userPages, inkindPage],
        mutateUserPages,
        async () => await followPage(userId, inkindRouteId)
      );
    }
  }

  async function handleUnfollowPage() {
    if (isPageOwnerOrOrganizer) {
      return;
    }

    const i = userPages.findIndex(page => page.routeId === inkindRouteId);
    const newUserPages = [...userPages];
    newUserPages.splice(i, 1);

    await optimisticUpdate(
      newUserPages,
      mutateUserPages,
      async () => await removeOrUnfollowPage(userId, inkindRouteId)
    );
  }

  const heartIcon = <SvgIcon {...bem('heart-icon')} name="Heart" />;
  const heartIconOutline = <SvgIcon {...bem('heart-icon-outline')} name="HeartOutline" />;

  async function handleClick() {
    if (isFollowing) {
      await handleUnfollowPage();
    } else {
      await handleFollowPage();
    }
  }

  return (
    <>
      {!isPageOwnerOrOrganizer ? (
        <div {...bem(null, [{ following: isFollowing }], className)}>
          {inkindPage && (
            <div>
              <HoverAnimation>
                <Badge variant="white" pill {...bem('button-follow-unfollow')} onClick={handleClick}>
                  <div {...bem('icon-container')}>
                    {heartIcon}
                    {heartIconOutline}
                  </div>
                  <span {...bem('main-copy')}>
                    {t(
                      isFollowing
                        ? translationKeys.FollowPageCTAFollowingLabel
                        : translationKeys.FollowPageCTAFollowLabel
                    )}
                  </span>
                  <span {...bem('hover-copy')}>
                    {t(
                      isFollowing
                        ? translationKeys.FollowPageCTAUnfollowLabel
                        : translationKeys.FollowPageCTAFollowLabel
                    )}
                  </span>
                </Badge>
              </HoverAnimation>
            </div>
          )}
        </div>
      ) : null}
    </>
  );
}
